import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Filter,
  ShieldCheck,
  EyeOff,
  Ghost,
  Smartphone,
  Usb,
  Shirt,
  Package,
} from "lucide-react";
import { PIXELS, TIERS, ACCESSORIES, CATEGORIES, formatAUD, priceFor } from "../mock";

const tierIcon = {
  degoogled: ShieldCheck,
  stealth: EyeOff,
  phantom: Ghost,
};

const catIcon = {
  phones: Smartphone,
  usb: Usb,
  case: Package,
  merch: Shirt,
};

const Shop = () => {
  const [category, setCategory] = useState("phones");
  const [tier, setTier] = useState("degoogled");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sortedPhones = useMemo(() => {
    const arr = [...PIXELS];
    if (sort === "price-asc") arr.sort((a, b) => a.base_aud - b.base_aud);
    if (sort === "price-desc") arr.sort((a, b) => b.base_aud - a.base_aud);
    if (sort === "newest") arr.sort((a, b) => b.year - a.year);
    return arr;
  }, [sort]);

  const filteredAccessories = useMemo(() => {
    const arr = ACCESSORIES.filter((a) => a.category === category);
    const withMin = arr.map((a) => ({
      ...a,
      minPrice: Math.min(...a.variants.map((v) => v.price)),
    }));
    if (sort === "price-asc") withMin.sort((a, b) => a.minPrice - b.minPrice);
    if (sort === "price-desc") withMin.sort((a, b) => b.minPrice - a.minPrice);
    return withMin;
  }, [category, sort]);

  const isPhones = category === "phones";

  return (
    <main className="min-h-screen bg-black text-white pt-24">
      {/* Header */}
      <section className="relative pb-6">
        <div className="absolute inset-0 aurora-purple opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-10">
          <div className="chip">Shop</div>
          <h1 className="mt-4 font-headline text-5xl md:text-7xl font-bold text-gradient-purple leading-[0.95]">
            {isPhones ? (
              <>Every Pixel.<br />Every tier.</>
            ) : (
              <>Everything else<br />you'll need.</>
            )}
          </h1>
          <p className="mt-4 text-white/60 max-w-xl">
            {isPhones
              ? "Choose a device, then choose your level of privacy. Prices shown are for the selected tier configuration."
              : "Encrypted USBs, hardened boot media, cases and merch. Built and packed with the same paranoia as our phones."}
          </p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {CATEGORIES.map((c) => {
            const Ico = catIcon[c.id];
            const active = category === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-all whitespace-nowrap ${
                  active
                    ? "bg-purple-500 text-black border-purple-400"
                    : "bg-white/5 text-white/70 border-white/10 hover:border-purple-500/40 hover:text-white"
                }`}
              >
                <Ico className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Controls */}
      <section className="sticky top-14 z-30 backdrop-blur-xl bg-black/70 border-y border-white/5 mt-6">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          {isPhones ? (
            <div className="flex items-center gap-2 bg-white/5 rounded-full p-1 border border-white/10">
              {TIERS.map((t) => {
                const Ico = tierIcon[t.id];
                const active = tier === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTier(t.id)}
                    className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all inline-flex items-center gap-2 ${
                      active ? "bg-purple-500 text-black" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Ico className="h-3.5 w-3.5" />
                    {t.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-xs uppercase tracking-[0.2em] text-white/40">
              {filteredAccessories.length} {filteredAccessories.length === 1 ? "product" : "products"}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Filter className="h-4 w-4 text-white/40" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white/80 focus:outline-none focus:border-purple-500 transition-colors"
            >
              {isPhones && <option value="newest">Newest first</option>}
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-14">
        {isPhones ? (
          <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPhones.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                className="group relative rounded-3xl overflow-hidden card-glass hover-lift"
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-700 opacity-70"
                    style={{ backgroundImage: `url(${p.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 chip">{p.year}</div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-headline text-2xl font-bold text-white">{p.name}</h3>
                      <p className="text-sm text-white/50 mt-1">{p.tagline}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3 text-xs text-white/50">
                    <span>{p.chip}</span>
                    <span className="h-3 w-px bg-white/20" />
                    <span>{p.ram}</span>
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300">
                        {TIERS.find((t) => t.id === tier).name} tier
                      </div>
                      <div className="font-display text-2xl font-bold text-white mt-1">
                        {formatAUD(priceFor(p.base_aud, tier))}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm text-purple-300 group-hover:text-white transition-colors">
                      Configure <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAccessories.map((a) => (
              <Link
                key={a.id}
                to={`/product/${a.id}`}
                className="group relative rounded-3xl overflow-hidden card-glass hover-lift"
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <div
                    className="absolute inset-0 transform group-hover:scale-105 transition-transform duration-700 opacity-90"
                    style={{
                      backgroundImage: `url(${a.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      ...(a.imageStyle || {}),
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 chip">
                    {CATEGORIES.find((c) => c.id === a.category)?.label}
                  </div>
                  {a.popular && (
                    <div className="absolute top-4 right-4 chip bg-purple-500 text-black border-purple-400">
                      Popular
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-headline text-2xl font-bold text-white">{a.name}</h3>
                  <p className="text-sm text-white/50 mt-1">{a.tagline}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300">
                        From
                      </div>
                      <div className="font-display text-2xl font-bold text-white mt-1">
                        {formatAUD(a.minPrice)}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm text-purple-300 group-hover:text-white transition-colors">
                      View <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Shop;
